import { useEffect, useState } from "react"
import { useQuery } from "react-query"
import { searchLocations } from "../requests/search"
import type { EventLocation, SearchLocationsResponse } from "../types/search"

export const LOCATION_SEARCH_QUERY_KEY = "location-search"
const DEBOUNCE_MS = 300

interface UseLocationSearchParams {
  query: string
  enabled?: boolean
}

interface UseLocationSearchResult {
  locations: EventLocation[]
  isLoading: boolean
  error: Error | null
}

/**
 * Debounced combined venue + Google Places search, backed by
 * GET /api/v1/search/locations (the merge/priority logic lives server-side
 * so it isn't duplicated between web and mobile clients).
 */
export function useLocationSearch({
  query,
  enabled = true,
}: UseLocationSearchParams): UseLocationSearchResult {
  const [debouncedQuery, setDebouncedQuery] = useState(query)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  const trimmed = debouncedQuery.trim()
  const queryEnabled = enabled && trimmed.length > 0

  const { data, isLoading, error } = useQuery<SearchLocationsResponse>(
    [LOCATION_SEARCH_QUERY_KEY, trimmed],
    () => searchLocations(trimmed),
    {
      enabled: queryEnabled,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 1,
    },
  )

  return {
    locations: data?.locations || [],
    isLoading: queryEnabled && isLoading,
    error: error as Error | null,
  }
}
